import { useCallback, useEffect, useRef, useState } from "react"
import { useParams, useNavigate, useSearchParams } from "react-router-dom"
import { useSelector } from "react-redux"
import { toast } from "react-toastify"
import { selectCurrentBranch } from "../../../core/data/redux/slice/branchesSlice"
import {
  templatesApi,
  masterTemplatesApi,
  signaturesApi,
  fontsApi,
  uploadDesignerAssetApi,
  assetUrlApi,
  samplePdfApi,
  siteSettingsApi,
} from "../../../service/certificate"
import { all_routes } from "../../router/all_routes"
import { fieldsForType } from "./fieldRegistry"
import { useCustomFontFaces } from "../common/useCustomFontFaces"
import { FEE_STICKER_SAMPLE_VARS, resolveStickerText, stickerTextStyle } from "./stickerRender"

type Layer = any

const BUILTIN_FONTS = [
  "Georgia, serif",
  "Times New Roman, serif",
  "Arial, sans-serif",
  "Helvetica, sans-serif",
  "Garamond, serif",
  "Courier New, monospace",
]

const uid = () =>
  (crypto as any)?.randomUUID?.() || `l${Date.now()}${Math.random().toString(36).slice(2, 7)}`

const DISPLAY_W = 860 // px the canvas is shown at when zoom is 100%; coords scale from this
const MIN_SIZE = 8 // smallest a layer can be resized to, in canvas px
const SNAP_PX = 6 // snap catch radius in *screen* px, independent of zoom level
const ZOOM_MIN = 25
const ZOOM_MAX = 400
const ZOOM_STEP = 25

// <input type=color> only accepts #rrggbb — guard non-hex (e.g. rgba) values.
const asHex = (c: any) => (typeof c === "string" && /^#[0-9a-fA-F]{6}$/.test(c) ? c : "#000000")

type ResizeDir = "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw"
const RESIZE_HANDLES: { dir: ResizeDir; style: React.CSSProperties }[] = [
  { dir: "nw", style: { left: -5, top: -5, cursor: "nwse-resize" } },
  { dir: "n", style: { left: "50%", top: -5, marginLeft: -5, cursor: "ns-resize" } },
  { dir: "ne", style: { right: -5, top: -5, cursor: "nesw-resize" } },
  { dir: "e", style: { right: -5, top: "50%", marginTop: -5, cursor: "ew-resize" } },
  { dir: "se", style: { right: -5, bottom: -5, cursor: "nwse-resize" } },
  { dir: "s", style: { left: "50%", bottom: -5, marginLeft: -5, cursor: "ns-resize" } },
  { dir: "sw", style: { left: -5, bottom: -5, cursor: "nesw-resize" } },
  { dir: "w", style: { left: -5, top: "50%", marginTop: -5, cursor: "ew-resize" } },
]

// Layers use the render-engine schema: x / y / w / h (not width/height),
// type text | dynamic-text | image | signature | shape | line.
const CertDesigner = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isMaster = searchParams.get("master") === "1"
  const backTarget = searchParams.get("back")
  const currentBranch = useSelector(selectCurrentBranch)
  const api = isMaster ? masterTemplatesApi : templatesApi
  // Not feeStickerDesigner -- that route just re-detects the existing
  // template and immediately navigates back in here, so "Back" would bounce
  // straight back to this same editor instead of actually leaving it.
  const backRoute = backTarget === "fee-sticker"
    ? all_routes.feeStickerPrint
    : isMaster
      ? all_routes.certMasterTemplates
      : all_routes.certTemplates
  const [tpl, setTpl] = useState<any>(null)
  const [canvas, setCanvas] = useState({ width: 1152, height: 960, background: "#ffffff" })
  const [layers, setLayers] = useState<Layer[]>([])
  const [selId, setSelId] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [sampleBusy, setSampleBusy] = useState(false)
  const [signatures, setSignatures] = useState<any[]>([])
  const [fonts, setFonts] = useState<any[]>([])
  const [assetUrls, setAssetUrls] = useState<Record<string, string>>({})
  const [siteSettings, setSiteSettings] = useState<Record<string, string>>({})
  const [showA4Grid, setShowA4Grid] = useState(false)
  // Pixel-perfect aids: zoom level, background grid, and snap-to-grid.
  const [zoomPct, setZoomPct] = useState(100)
  const [showGrid, setShowGrid] = useState(true)
  const [snapToGrid, setSnapToGrid] = useState(true)
  const [gridSize, setGridSize] = useState(10)
  // Alignment guide lines shown while dragging/resizing { x: [], y: [] } (canvas coords)
  const [guides, setGuides] = useState<{ x: number[]; y: number[] }>({ x: [], y: [] })
  // Live "x, y · w × h" readout shown next to the layer while dragging/resizing.
  const [dragBadge, setDragBadge] = useState<{ x: number; y: number; w: number; h: number } | null>(null)

  // Refs mirroring the latest state so the window-level mouse/keyboard
  // listeners (registered once) never read stale closures.
  const layersRef = useRef<Layer[]>([])
  useEffect(() => { layersRef.current = layers }, [layers])
  const canvasRef = useRef(canvas)
  useEffect(() => { canvasRef.current = canvas }, [canvas])
  const selIdRef = useRef<string | null>(null)
  useEffect(() => { selIdRef.current = selId }, [selId])
  const snapToGridRef = useRef(snapToGrid)
  useEffect(() => { snapToGridRef.current = snapToGrid }, [snapToGrid])
  const gridSizeRef = useRef(gridSize)
  useEffect(() => { gridSizeRef.current = Math.max(1, gridSize || 10) }, [gridSize])

  // Inject @font-face for uploaded fonts so layers using a custom family
  // render with the real typeface on the canvas (not the system fallback).
  useCustomFontFaces(fonts)

  const baseScale = DISPLAY_W / canvas.width
  const scale = baseScale * (zoomPct / 100)
  const sel = layers.find(l => l.id === selId) || null
  const certType = tpl?.certificateType || "general"
  // Fee-sticker templates get a dedicated WYSIWYG preview path — see below.
  const isFeeSticker = certType === "fee_sticker"
  const stickerVars: Record<string, string> = isFeeSticker
    ? { ...FEE_STICKER_SAMPLE_VARS, ...Object.fromEntries(Object.entries(siteSettings).filter(([, v]) => v)) }
    : {}

  useEffect(() => {
    if (!id) return
    ;(async () => {
      try {
        const sigParams = currentBranch?._id ? { branch: currentBranch._id } : {}
        const [t, sig, fnt, settings] = await Promise.all([
          api.get(id),
          signaturesApi.list(sigParams),
          fontsApi.list(),
          siteSettingsApi.get().catch(() => ({ data: {} })),
        ])
        setTpl(t.data)
        const def = t.data?.definition || {}
        setCanvas({
          width: def.canvas?.width || 1152,
          height: def.canvas?.height || 960,
          background: def.canvas?.background || def.background_color || "#ffffff",
        })
        setLayers(Array.isArray(def.layers) ? def.layers : [])
        setSignatures(sig.data || [])
        setFonts(fnt.data || [])
        const s = (settings as any)?.data || {}
        setSiteSettings({
          "school.name": s.schoolName || "",
          "school.code": s.schoolCode || "",
          "campus.address": s.address || "",
          "campus.phone": s.phone || "",
        })
      } catch (err: any) {
        toast.error(err?.response?.data?.message || "Failed to load template")
      }
    })()
  }, [id])

  const ensureAssetUrl = useCallback(
    async (key: string) => {
      if (!key || assetUrls[key]) return
      try {
        const r = await assetUrlApi(key)
        if (r.success) setAssetUrls(prev => ({ ...prev, [key]: r.url }))
      } catch {
        /* ignore */
      }
    },
    [assetUrls]
  )
  useEffect(() => {
    layers.forEach(l => {
      if (l.type === "image" && l.src) ensureAssetUrl(l.src)
      if (l.type === "signature" && l.signatureId) {
        const s = signatures.find(x => x._id === l.signatureId)
        if (s?.imageKey) ensureAssetUrl(s.imageKey)
      }
    })
  }, [layers, signatures, ensureAssetUrl])

  // ── Layer ops ────────────────────────────────────────────────────
  const update = (lid: string, patch: Partial<Layer>) =>
    setLayers(prev => prev.map(l => (l.id === lid ? { ...l, ...patch } : l)))
  const updateSel = (patch: Partial<Layer>) => sel && update(sel.id, patch)

  const addLayer = (layer: Layer) => {
    const l = { id: uid(), x: 80, y: 80, w: 360, h: 90, rotation: 0, opacity: 1, ...layer }
    setLayers(prev => [...prev, l])
    setSelId(l.id)
  }
  const textBase = {
    fontFamily: "Georgia, serif",
    fontSize: 36,
    fontWeight: "normal",
    fontStyle: "normal",
    color: "#111827",
    textAlign: "center",
    verticalAlign: "middle",
    lineHeight: 1.15,
    autoFit: true,
  }
  const addText = (content = "Text", extra: Partial<Layer> = {}) =>
    addLayer({ type: "text", content, ...textBase, ...extra })
  const addField = () =>
    addLayer({
      type: "dynamic-text",
      field: fieldsForType(certType)[0]?.key || "student.full_name",
      prefix: "",
      suffix: "",
      ...textBase,
    })
  const addImage = () => addLayer({ type: "image", src: "", fit: "contain", w: 240, h: 160 })
  const addSignature = () =>
    addLayer({
      type: "signature",
      signatureId: signatures[0]?._id || "",
      showName: true,
      showTitle: true,
      color: "#111827",
      fontFamily: "Georgia, serif",
      fontSize: 16,
      w: 220,
      h: 110,
    })
  const addShape = () =>
    addLayer({ type: "shape", shape: "rectangle", fill: "transparent", stroke: "#0f172a", strokeWidth: 2, borderRadius: 0, w: 300, h: 180 })
  const addLine = () => addLayer({ type: "line", color: "#111827", thickness: 3, rounded: true, w: 300, h: 4 })

  const deleteLayer = (lid: string) => {
    const layer = layers.find(l => l.id === lid)
    if (layer?._inherited) return // master-template layers are read-only
    setLayers(prev => prev.filter(l => l.id !== lid))
    if (selId === lid) setSelId(null)
  }
  const move = (lid: string, dir: -1 | 1) =>
    setLayers(prev => {
      const i = prev.findIndex(l => l.id === lid)
      if (i < 0 || prev[i]?._inherited) return prev // can't reorder inherited layers
      const j = i + dir
      if (j < 0 || j >= prev.length) return prev
      const next = [...prev]
      ;[next[i], next[j]] = [next[j], next[i]]
      return next
    })
  const insertField = (key: string) => {
    if (!sel || sel.type !== "text") {
      toast.info("Select a text layer (or use a Field layer).")
      return
    }
    updateSel({ content: `${sel.content || ""}{{${key}}}` })
  }

  // ── Drag & resize — smooth, rAF-batched, snap-to-grid + snap-to-layers ──
  // Mouse events fire far more often than the screen repaints; committing a
  // setState on every single event makes big templates feel laggy. Instead
  // we compute the next position/size on every mousemove but only commit it
  // once per animation frame, so dragging feels as smooth as Figma
  // regardless of how many layers are on the canvas.
  const dragRef = useRef<{ id: string; startX: number; startY: number; ox: number; oy: number } | null>(null)
  const resizeRef = useRef<{ id: string; dir: ResizeDir; startX: number; startY: number; ox: number; oy: number; ow: number; oh: number } | null>(null)
  const rafRef = useRef<number | null>(null)
  const pendingPatchRef = useRef<{ id: string; patch: Partial<Layer> } | null>(null)
  const pendingGuidesRef = useRef<{ x: number[]; y: number[] } | null>(null)
  const pendingBadgeRef = useRef<{ x: number; y: number; w: number; h: number } | null>(null)

  const onLayerMouseDown = (e: React.MouseEvent, l: Layer) => {
    e.stopPropagation()
    // Inherited layers (from master template) are read-only — skip selection
    if (l._inherited) return
    setSelId(l.id)
    if (l.locked) return
    dragRef.current = { id: l.id, startX: e.clientX, startY: e.clientY, ox: l.x, oy: l.y }
    document.body.style.cursor = "grabbing"
    document.body.style.userSelect = "none"
  }
  const onHandleMouseDown = (e: React.MouseEvent, l: Layer, dir: ResizeDir) => {
    e.stopPropagation()
    if (l._inherited || l.locked) return
    resizeRef.current = { id: l.id, dir, startX: e.clientX, startY: e.clientY, ox: l.x || 0, oy: l.y || 0, ow: l.w || 0, oh: l.h || 0 }
    document.body.style.cursor = RESIZE_HANDLES.find(h => h.dir === dir)?.style.cursor as string || "default"
    document.body.style.userSelect = "none"
  }

  useEffect(() => {
    const SNAP = SNAP_PX / scale

    const snapTargets = (excludeId: string) => {
      const c = canvasRef.current
      const xs: number[] = [0, c.width / 2, c.width]
      const ys: number[] = [0, c.height / 2, c.height]
      for (const o of layersRef.current) {
        if (o.id === excludeId || o._inherited) continue
        const ow = o.w || 0
        const oh = o.h || 0
        xs.push(o.x || 0, (o.x || 0) + ow / 2, (o.x || 0) + ow)
        ys.push(o.y || 0, (o.y || 0) + oh / 2, (o.y || 0) + oh)
      }
      return { xs, ys }
    }

    const scheduleFlush = () => {
      if (rafRef.current != null) return
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null
        if (pendingPatchRef.current) {
          const { id, patch } = pendingPatchRef.current
          setLayers(prev => prev.map(l => (l.id === id ? { ...l, ...patch } : l)))
          pendingPatchRef.current = null
        }
        if (pendingGuidesRef.current) {
          setGuides(pendingGuidesRef.current)
          pendingGuidesRef.current = null
        }
        if (pendingBadgeRef.current) {
          setDragBadge(pendingBadgeRef.current)
          pendingBadgeRef.current = null
        }
      })
    }

    const handleMove = (e: MouseEvent) => {
      const d = dragRef.current
      if (!d) return
      const dragged = layersRef.current.find(l => l.id === d.id)
      if (!dragged) return
      const w = dragged.w ?? 0
      const h = dragged.h ?? 0
      let nx = d.ox + (e.clientX - d.startX) / scale
      let ny = d.oy + (e.clientY - d.startY) / scale

      const { xs, ys } = snapTargets(d.id)
      const hitX: number[] = []
      const hitY: number[] = []
      const candidatesX = [nx, nx + w / 2, nx + w]
      const candidatesY = [ny, ny + h / 2, ny + h]
      for (const sx of xs) for (const cx of candidatesX) if (Math.abs(cx - sx) < SNAP) { nx += sx - cx; hitX.push(sx) }
      for (const sy of ys) for (const cy of candidatesY) if (Math.abs(cy - sy) < SNAP) { ny += sy - cy; hitY.push(sy) }

      if (snapToGridRef.current) {
        const g = gridSizeRef.current
        if (!hitX.length) nx = Math.round(nx / g) * g
        if (!hitY.length) ny = Math.round(ny / g) * g
      }
      nx = Math.round(nx)
      ny = Math.round(ny)

      pendingPatchRef.current = { id: d.id, patch: { x: nx, y: ny } }
      pendingGuidesRef.current = { x: Array.from(new Set(hitX)), y: Array.from(new Set(hitY)) }
      pendingBadgeRef.current = { x: nx, y: ny, w, h }
      scheduleFlush()
    }

    const handleResize = (e: MouseEvent) => {
      const r = resizeRef.current
      if (!r) return
      const dx = (e.clientX - r.startX) / scale
      const dy = (e.clientY - r.startY) / scale
      const { xs, ys } = snapTargets(r.id)
      let nx = r.ox
      let ny = r.oy
      let nw = r.ow
      let nh = r.oh
      const hitX: number[] = []
      const hitY: number[] = []

      if (r.dir.includes("e")) {
        let right = r.ox + r.ow + dx
        for (const sx of xs) if (Math.abs(right - sx) < SNAP) { right = sx; hitX.push(sx) }
        nw = Math.max(MIN_SIZE, right - r.ox)
      }
      if (r.dir.includes("w")) {
        let left = r.ox + dx
        for (const sx of xs) if (Math.abs(left - sx) < SNAP) { left = sx; hitX.push(sx) }
        const right = r.ox + r.ow
        nw = Math.max(MIN_SIZE, right - left)
        nx = right - nw
      }
      if (r.dir.includes("s")) {
        let bottom = r.oy + r.oh + dy
        for (const sy of ys) if (Math.abs(bottom - sy) < SNAP) { bottom = sy; hitY.push(sy) }
        nh = Math.max(MIN_SIZE, bottom - r.oy)
      }
      if (r.dir.includes("n")) {
        let top = r.oy + dy
        for (const sy of ys) if (Math.abs(top - sy) < SNAP) { top = sy; hitY.push(sy) }
        const bottom = r.oy + r.oh
        nh = Math.max(MIN_SIZE, bottom - top)
        ny = bottom - nh
      }

      if (snapToGridRef.current) {
        const g = gridSizeRef.current
        if (r.dir.includes("e") && !hitX.length) nw = Math.max(MIN_SIZE, Math.round((r.ox + nw) / g) * g - r.ox)
        if (r.dir.includes("w") && !hitX.length) {
          const left = Math.round(nx / g) * g
          nw = Math.max(MIN_SIZE, r.ox + r.ow - left)
          nx = r.ox + r.ow - nw
        }
        if (r.dir.includes("s") && !hitY.length) nh = Math.max(MIN_SIZE, Math.round((r.oy + nh) / g) * g - r.oy)
        if (r.dir.includes("n") && !hitY.length) {
          const top = Math.round(ny / g) * g
          nh = Math.max(MIN_SIZE, r.oy + r.oh - top)
          ny = r.oy + r.oh - nh
        }
      }

      nx = Math.round(nx); ny = Math.round(ny); nw = Math.round(nw); nh = Math.round(nh)
      pendingPatchRef.current = { id: r.id, patch: { x: nx, y: ny, w: nw, h: nh } }
      pendingGuidesRef.current = { x: Array.from(new Set(hitX)), y: Array.from(new Set(hitY)) }
      pendingBadgeRef.current = { x: nx, y: ny, w: nw, h: nh }
      scheduleFlush()
    }

    const onMouseMove = (e: MouseEvent) => {
      if (dragRef.current) handleMove(e)
      else if (resizeRef.current) handleResize(e)
    }
    const onUp = () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      // Flush whatever was pending so the final position is exact, not
      // whatever happened to be committed on the last animation frame.
      if (pendingPatchRef.current) {
        const { id, patch } = pendingPatchRef.current
        setLayers(prev => prev.map(l => (l.id === id ? { ...l, ...patch } : l)))
        pendingPatchRef.current = null
      }
      pendingGuidesRef.current = null
      pendingBadgeRef.current = null
      dragRef.current = null
      resizeRef.current = null
      document.body.style.cursor = ""
      document.body.style.userSelect = ""
      setGuides({ x: [], y: [] })
      setDragBadge(null)
    }
    window.addEventListener("mousemove", onMouseMove)
    window.addEventListener("mouseup", onUp)
    return () => {
      window.removeEventListener("mousemove", onMouseMove)
      window.removeEventListener("mouseup", onUp)
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
    }
  }, [scale])

  // ── Keyboard: arrow-key nudge, Delete, Escape ───────────────────────
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement
      const tag = target?.tagName
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || target?.isContentEditable) return
      const id = selIdRef.current
      if (e.key === "Escape") { setSelId(null); return }
      if (!id) return
      if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault()
        setLayers(prev => {
          const layer = prev.find(l => l.id === id)
          if (!layer || layer._inherited) return prev
          return prev.filter(l => l.id !== id)
        })
        setSelId(null)
        return
      }
      const step = e.shiftKey ? 10 : 1
      const dirs: Record<string, [number, number]> = {
        ArrowUp: [0, -step],
        ArrowDown: [0, step],
        ArrowLeft: [-step, 0],
        ArrowRight: [step, 0],
      }
      const delta = dirs[e.key]
      if (!delta) return
      e.preventDefault()
      const [dx, dy] = delta
      setLayers(prev =>
        prev.map(l => (l.id === id && !l._inherited && !l.locked ? { ...l, x: (l.x || 0) + dx, y: (l.y || 0) + dy } : l))
      )
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [])

  const onUploadImage = async (file: File) => {
    if (!sel) return
    try {
      const r = await uploadDesignerAssetApi(file)
      if (r.success) {
        update(sel.id, { src: r.key })
        if (r.url) setAssetUrls(prev => ({ ...prev, [r.key]: r.url }))
        toast.success("Image uploaded")
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Upload failed")
    }
  }

  const buildDefinition = () => ({
    canvas,
    a4_grid: tpl?.definition?.a4_grid || null,
    background_color: canvas.background,
    // The render engine reads `textOpacity` for text layers (not the
    // outer `opacity`), so mirror it on save to keep PDF == preview.
    // Filter out _inherited layers — they belong to the master template
    // and are re-merged server-side on every read, so saving them here
    // would duplicate them as own layers.
    layers: layers
      .filter(l => !l._inherited)
      .map(l =>
        l.type === "text" || l.type === "dynamic-text"
          ? { ...l, textOpacity: l.opacity ?? 1 }
          : l
      ),
  })

  const save = async () => {
    if (!id) return
    setSaving(true)
    try {
      await api.update(id, { definition: buildDefinition() })
      toast.success("Template saved")
      navigate(backRoute)
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Save failed")
    } finally {
      setSaving(false)
    }
  }

  const samplePdf = async () => {
    setSampleBusy(true)
    try {
      const res = await samplePdfApi(buildDefinition(), certType)
      if (res.success && res.url) window.open(res.url, "_blank")
      else toast.error("Could not render sample")
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Sample render failed")
    } finally {
      setSampleBusy(false)
    }
  }

  // Replace {{token}} placeholders that have known static values (site settings)
  // so the canvas preview shows realistic content for layout purposes.
  // Student/session/event tokens stay as {{label}} since they vary per certificate.
  const resolvePreviewText = (text: string) => {
    if (!text) return text
    return text.replace(/\{\{([^}]+)\}\}/g, (_match, token) => {
      const resolved = siteSettings[token.trim()]
      return resolved !== undefined && resolved !== "" ? resolved : `{{${token}}}`
    })
  }

  // ── Canvas preview (approximate; PDF is exact via render engine) ──
  const textStyle = (l: Layer): React.CSSProperties => {
    const grad = l.gradient?.enabled
    return {
      width: "100%",
      height: "100%",
      display: "flex",
      alignItems: l.verticalAlign === "top" ? "flex-start" : l.verticalAlign === "bottom" ? "flex-end" : "center",
      justifyContent: l.textAlign === "left" ? "flex-start" : l.textAlign === "right" ? "flex-end" : "center",
      fontFamily: l.fontFamily,
      fontSize: (l.fontSize || 24) * scale,
      fontWeight: l.fontWeight,
      fontStyle: l.fontStyle,
      color: grad ? "transparent" : l.color,
      background: grad
        ? `linear-gradient(${l.gradient.angle ?? 90}deg, ${l.gradient.from || "#000"}, ${l.gradient.to || "#000"})`
        : undefined,
      WebkitBackgroundClip: grad ? "text" : undefined,
      backgroundClip: grad ? "text" : undefined,
      textAlign: l.textAlign,
      lineHeight: l.lineHeight || 1.15,
      letterSpacing: (l.letterSpacing || 0) * scale,
      whiteSpace: "pre-wrap",
      padding: 4 * scale,
      overflow: "hidden",
      textShadow:
        l.shadow?.enabled
          ? `${(l.shadow.x || 0) * scale}px ${(l.shadow.y || 0) * scale}px ${(l.shadow.blur || 0) * scale}px ${l.shadow.color || "rgba(0,0,0,0.4)"}`
          : undefined,
      WebkitTextStroke: l.stroke?.enabled
        ? `${(l.stroke.width || 1) * scale}px ${l.stroke.color || "#000000"}`
        : undefined,
    }
  }

  const renderContent = (l: Layer) => {
    // Fee-sticker text uses the exact same sizing/layout function as the
    // real print output (stickerTextStyle, shared via stickerRender.ts) fed
    // with realistic sample values, instead of the generic wrapped-text
    // preview below -- so what's shown here while editing is what prints.
    if (isFeeSticker && l.type === "text") {
      const value = resolveStickerText(l.content, stickerVars)
      return <div style={stickerTextStyle(l, scale, value)}>{value}</div>
    }
    if (isFeeSticker && l.type === "dynamic-text") {
      const value = `${l.prefix || ""}${stickerVars[l.field] ?? ""}${l.suffix || ""}`
      return (
        <div style={stickerTextStyle(l, scale, value)}>
          <span style={{ textDecoration: l.textDecoration }}>{value}</span>
        </div>
      )
    }
    if (l.type === "text") return <div style={textStyle(l)}>{resolvePreviewText(l.content)}</div>
    if (l.type === "dynamic-text") {
      // Resolve static site-setting tokens (school.name, campus.address, etc.).
      // Student/session/event fields show as {{label}} since they vary per recipient.
      const resolved = siteSettings[l.field]
      const display = resolved !== undefined && resolved !== ""
        ? `${l.prefix || ""}${resolved}${l.suffix || ""}`
        : `${l.prefix || ""}{{${fieldsForType(certType).find(f => f.key === l.field)?.label || l.field}}}${l.suffix || ""}`
      return <div style={textStyle(l)}>{display}</div>
    }
    if (l.type === "image") {
      const url = l.src ? assetUrls[l.src] : ""
      return url ? (
        <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: l.fit || "contain" }} />
      ) : (
        <div className="d-flex align-items-center justify-content-center text-muted bg-light h-100 w-100" style={{ fontSize: 12 }}>
          No image
        </div>
      )
    }
    if (l.type === "signature") {
      const s = signatures.find(x => x._id === l.signatureId)
      const url = s?.imageKey ? assetUrls[s.imageKey] : ""
      return (
        <div className="d-flex flex-column align-items-center justify-content-end h-100 w-100" style={{ color: l.color }}>
          {url ? (
            <img src={url} alt="" style={{ maxWidth: "100%", maxHeight: l.showName || l.showTitle ? "60%" : "100%", objectFit: "contain" }} />
          ) : (
            <div className="text-muted" style={{ fontSize: 11 }}>signature</div>
          )}
          <div style={{ borderTop: "1px solid currentColor", width: "100%", textAlign: "center", fontSize: (l.fontSize || 14) * scale }}>
            {l.showName && <div>{s?.signerName || "Name"}</div>}
            {l.showTitle && <div style={{ fontSize: "0.85em", opacity: 0.8 }}>{s?.signerTitle || "Title"}</div>}
          </div>
        </div>
      )
    }
    if (l.type === "shape") {
      return (
        <div
          style={{
            width: "100%",
            height: "100%",
            background: l.gradient?.enabled
              ? `linear-gradient(${l.gradient.angle ?? 90}deg, ${l.gradient.from || "#000"}, ${l.gradient.to || "#000"})`
              : (l.fill || "transparent"),
            border: l.strokeWidth > 0 ? `${l.strokeWidth * scale}px solid ${l.stroke}` : "none",
            borderRadius: l.shape === "ellipse" ? "50%" : (l.borderRadius || 0) * scale,
            boxSizing: "border-box",
          }}
        />
      )
    }
    if (l.type === "line") {
      return <div style={{ width: "100%", height: "100%", background: l.color, borderRadius: l.rounded ? "999px" : 0 }} />
    }
    return null
  }

  const isText = sel && (sel.type === "text" || sel.type === "dynamic-text")
  const zoomIn = () => setZoomPct(z => Math.min(ZOOM_MAX, z + ZOOM_STEP))
  const zoomOut = () => setZoomPct(z => Math.max(ZOOM_MIN, z - ZOOM_STEP))

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <div>
            <h3 className="page-title mb-1">{tpl?.name || "Designer"}</h3>
            <span className="badge bg-primary-transparent">{certType}</span>
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-light" onClick={() => navigate(backRoute)}>Back</button>
            <button className="btn btn-outline-primary" onClick={samplePdf} disabled={sampleBusy}>
              {sampleBusy ? "Rendering..." : <><i className="ti ti-eye me-1" />Sample PDF</>}
            </button>
            <button className="btn btn-primary" onClick={save} disabled={saving}>
              {saving ? "Saving..." : "Save Template"}
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="card mb-3">
          <div className="card-body d-flex flex-wrap gap-2 align-items-center">
            <button className="btn btn-sm btn-outline-primary" onClick={() => addText("Heading", { fontSize: 56, fontWeight: "bold" })}>
              <i className="ti ti-heading me-1" />Heading
            </button>
            <button className="btn btn-sm btn-outline-primary" onClick={() => addText("Text")}>
              <i className="ti ti-text-size me-1" />Text
            </button>
            <button className="btn btn-sm btn-outline-primary" onClick={addField}>
              <i className="ti ti-variable me-1" />Field
            </button>
            <button className="btn btn-sm btn-outline-primary" onClick={addImage}>
              <i className="ti ti-photo me-1" />Image
            </button>
            <button className="btn btn-sm btn-outline-primary" onClick={addSignature}>
              <i className="ti ti-signature me-1" />Signature
            </button>
            <button className="btn btn-sm btn-outline-primary" onClick={addShape}>
              <i className="ti ti-square me-1" />Shape
            </button>
            <button className="btn btn-sm btn-outline-primary" onClick={addLine}>
              <i className="ti ti-line me-1" />Line
            </button>
            <div className="vr mx-1" />
            <span className="small text-muted">Canvas</span>
            <input type="number" className="form-control form-control-sm" style={{ width: 90 }} value={canvas.width}
              onChange={e => setCanvas({ ...canvas, width: Number(e.target.value) || 1152 })} title="Width" />
            <span>×</span>
            <input type="number" className="form-control form-control-sm" style={{ width: 90 }} value={canvas.height}
              onChange={e => setCanvas({ ...canvas, height: Number(e.target.value) || 960 })} title="Height" />
            <input type="color" className="form-control form-control-sm form-control-color" value={canvas.background}
              onChange={e => setCanvas({ ...canvas, background: e.target.value })} title="Background" />
            <div className="vr mx-1" />
            <button
              type="button"
              className={`btn btn-sm ${showA4Grid ? "btn-warning" : "btn-outline-secondary"}`}
              onClick={() => setShowA4Grid(v => !v)}
              title="Toggle A4 crop lines (shows where A4-mode will trim)"
            >
              <i className="ti ti-border-style me-1" />A4 Grid
            </button>
          </div>
          {/* Precision row: zoom + grid/snap — kept visually separate from the
              insert/canvas toolbar above so it doesn't compete for attention,
              but stays close to the canvas it controls. */}
          <div className="card-body pt-0 d-flex flex-wrap gap-2 align-items-center border-top">
            <span className="small text-muted">Zoom</span>
            <div className="btn-group btn-group-sm" role="group">
              <button type="button" className="btn btn-outline-secondary" onClick={zoomOut} disabled={zoomPct <= ZOOM_MIN} title="Zoom out">
                <i className="ti ti-minus" />
              </button>
              <button type="button" className="btn btn-outline-secondary" style={{ minWidth: 58, pointerEvents: "none" }}>
                {zoomPct}%
              </button>
              <button type="button" className="btn btn-outline-secondary" onClick={zoomIn} disabled={zoomPct >= ZOOM_MAX} title="Zoom in">
                <i className="ti ti-plus" />
              </button>
            </div>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setZoomPct(100)} title="Reset zoom to 100%">
              Reset
            </button>
            <div className="vr mx-1" />
            <button
              type="button"
              className={`btn btn-sm ${showGrid ? "btn-primary" : "btn-outline-secondary"}`}
              onClick={() => setShowGrid(v => !v)}
              title="Toggle background grid"
            >
              <i className="ti ti-grid-dots me-1" />Grid
            </button>
            <button
              type="button"
              className={`btn btn-sm ${snapToGrid ? "btn-primary" : "btn-outline-secondary"}`}
              onClick={() => setSnapToGrid(v => !v)}
              title="Snap moved/resized layers to the grid"
            >
              <i className="ti ti-magnet me-1" />Snap to grid
            </button>
            <input
              type="number"
              className="form-control form-control-sm"
              style={{ width: 70 }}
              min={2}
              value={gridSize}
              onChange={e => setGridSize(Math.max(2, Number(e.target.value) || 10))}
              title="Grid size (px)"
            />
            <span className="small text-muted d-none d-lg-inline">
              Drag layers to snap to the grid and to other elements' edges/centers. Arrow keys nudge 1px (Shift = 10px). Delete removes the selected layer.
            </span>
          </div>
        </div>

        <div className="row g-3">
          {/* Canvas */}
          <div className="col-lg-8">
            <div className="card">
              <div className="card-body d-flex justify-content-center" style={{ overflow: "auto", background: "#e9ecef" }}>
                <div
                  onMouseDown={() => setSelId(null)}
                  style={{
                    position: "relative",
                    width: canvas.width * scale,
                    height: canvas.height * scale,
                    background: canvas.background,
                    boxShadow: "0 0 0 1px #ccc",
                    flexShrink: 0,
                  }}
                >
                  {/* Background alignment grid — purely visual; actual snapping
                      happens against gridSize regardless of whether this is shown. */}
                  {showGrid && (
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        pointerEvents: "none",
                        zIndex: 1,
                        backgroundImage:
                          "linear-gradient(to right, rgba(37,99,235,0.14) 1px, transparent 1px)," +
                          "linear-gradient(to bottom, rgba(37,99,235,0.14) 1px, transparent 1px)",
                        backgroundSize: `${Math.max(2, gridSize) * scale}px ${Math.max(2, gridSize) * scale}px`,
                      }}
                    />
                  )}

                  {/* A4-grid crop lines — shows where A4-mode will trim the canvas */}
                  {showA4Grid && (() => {
                    const isPortrait = canvas.height > canvas.width
                    const a4W = (isPortrait ? 794 : 1123) * scale
                    const a4H = (isPortrait ? 1123 : 794) * scale
                    const cx = (canvas.width * scale) / 2
                    const cy = (canvas.height * scale) / 2
                    return (
                      <div
                        style={{
                          position: "absolute",
                          left: cx - a4W / 2,
                          top: cy - a4H / 2,
                          width: a4W,
                          height: a4H,
                          border: "2px dashed rgba(239,68,68,0.7)",
                          pointerEvents: "none",
                          zIndex: 999,
                          boxSizing: "border-box",
                        }}
                      >
                        <span style={{ position: "absolute", top: -18, left: 0, fontSize: 10, color: "rgba(239,68,68,0.9)", background: "rgba(255,255,255,0.8)", padding: "0 3px", borderRadius: 2 }}>A4 boundary</span>
                      </div>
                    )
                  })()}

                  {/* Alignment guides — vertical (x) and horizontal (y) snap lines */}
                  {guides.x.map(gx => (
                    <div key={`gx${gx}`} style={{ position: "absolute", left: gx * scale, top: 0, width: 1, height: "100%", background: "rgba(239,68,68,0.7)", pointerEvents: "none", zIndex: 998 }} />
                  ))}
                  {guides.y.map(gy => (
                    <div key={`gy${gy}`} style={{ position: "absolute", left: 0, top: gy * scale, width: "100%", height: 1, background: "rgba(239,68,68,0.7)", pointerEvents: "none", zIndex: 998 }} />
                  ))}

                  {layers.map(l => {
                    const isSel = selId === l.id
                    const editable = !l._inherited && !l.locked
                    return (
                      <div
                        key={l.id}
                        onMouseDown={e => onLayerMouseDown(e, l)}
                        style={{
                          position: "absolute",
                          left: (l.x || 0) * scale,
                          top: (l.y || 0) * scale,
                          width: (l.w || 0) * scale,
                          height: (l.h || 0) * scale,
                          outline: isSel ? "2px solid #2563eb" : "1px dashed transparent",
                          cursor: l._inherited ? "default" : l.locked ? "not-allowed" : "grab",
                          opacity: l.opacity ?? 1,
                          transform: l.rotation ? `rotate(${l.rotation}deg)` : undefined,
                          transformOrigin: l.pivotOrigin || "center center",
                        }}
                      >
                        {renderContent(l)}
                        {isSel && editable && RESIZE_HANDLES.map(h => (
                          <div
                            key={h.dir}
                            onMouseDown={e => onHandleMouseDown(e, l, h.dir)}
                            style={{
                              position: "absolute",
                              width: 10,
                              height: 10,
                              background: "#fff",
                              border: "1.5px solid #2563eb",
                              borderRadius: 2,
                              zIndex: 1001,
                              ...h.style,
                            }}
                          />
                        ))}
                        {isSel && dragBadge && (dragRef.current?.id === l.id || resizeRef.current?.id === l.id) && (
                          <div
                            style={{
                              position: "absolute",
                              left: 0,
                              top: -24,
                              background: "#111827",
                              color: "#fff",
                              fontFamily: "ui-monospace, monospace",
                              fontSize: 11,
                              padding: "2px 6px",
                              borderRadius: 4,
                              whiteSpace: "nowrap",
                              pointerEvents: "none",
                              zIndex: 1002,
                            }}
                          >
                            {dragBadge.x}, {dragBadge.y} · {dragBadge.w}×{dragBadge.h}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Right panel */}
          <div className="col-lg-4">
            <div className="card mb-3">
              <div className="card-header py-2"><h6 className="mb-0">Layers</h6></div>
              <div className="card-body p-2" style={{ maxHeight: 180, overflow: "auto" }}>
                {layers.length === 0 && <div className="text-muted small p-2">No layers yet.</div>}
                {layers.map((l, idx) => ({ l, idx })).reverse().map(({ l }) => (
                  <div
                    key={l.id}
                    className={`d-flex align-items-center justify-content-between px-2 py-1 rounded ${selId === l.id ? "bg-primary-transparent" : ""}`}
                    style={{ cursor: l._inherited ? "default" : "pointer", opacity: l._inherited ? 0.5 : 1 }}
                    onClick={() => { if (!l._inherited) setSelId(l.id) }}
                    title={l._inherited ? "Inherited from master template — read only" : undefined}
                  >
                    <span className="small text-truncate" style={{ maxWidth: 150 }}>
                      <i className={`ti ti-${l._inherited ? "lock" : l.type === "text" ? "text-size" : l.type === "dynamic-text" ? "variable" : l.type === "image" ? "photo" : l.type === "signature" ? "signature" : l.type === "line" ? "line" : "square"} me-1`} />
                      {l.type === "text" ? l.content?.slice(0, 16) || "text" : l.type === "dynamic-text" ? l.field : l.type}
                    </span>
                    {l._inherited ? (
                      <span className="small text-muted" title="From master template">master</span>
                    ) : (
                      <span className="d-flex gap-1">
                        <i className="ti ti-arrow-up text-muted" onClick={e => { e.stopPropagation(); move(l.id, 1) }} />
                        <i className="ti ti-arrow-down text-muted" onClick={e => { e.stopPropagation(); move(l.id, -1) }} />
                        <i className="ti ti-trash text-danger" onClick={e => { e.stopPropagation(); deleteLayer(l.id) }} />
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="card">
              <div className="card-header py-2"><h6 className="mb-0">Properties</h6></div>
              <div className="card-body" style={{ maxHeight: 480, overflow: "auto" }}>
                {!sel && <div className="text-muted small">Select a layer to edit. Drag its handles to resize, drag its body to move — both snap to the grid and to other layers.</div>}
                {sel?._inherited && (
                  <div className="alert alert-warning py-1 px-2 small mb-2">
                    <i className="ti ti-lock me-1" />
                    Inherited from master template — edit the master to change this layer.
                  </div>
                )}
                {sel && (
                  <>
                    <div className="row g-2 mb-2">
                      {(["x", "y", "w", "h"] as const).map(k => (
                        <div className="col-6" key={k}>
                          <label className="form-label small mb-0 text-uppercase">{k}</label>
                          <input type="number" className="form-control form-control-sm" value={sel[k] ?? 0}
                            onChange={e => updateSel({ [k]: Number(e.target.value) } as any)} />
                        </div>
                      ))}
                      <div className="col-6">
                        <label className="form-label small mb-0">Rotation</label>
                        <input type="number" className="form-control form-control-sm" value={sel.rotation ?? 0}
                          onChange={e => updateSel({ rotation: Number(e.target.value) })} />
                      </div>
                      <div className="col-6">
                        <label className="form-label small mb-0">Opacity</label>
                        <input type="number" min={0} max={1} step={0.1} className="form-control form-control-sm" value={sel.opacity ?? 1}
                          onChange={e => updateSel({ opacity: Number(e.target.value) })} />
                      </div>
                      <div className="col-12">
                        <div className="form-check mt-1">
                          <input className="form-check-input" type="checkbox" id="lock" checked={!!sel.locked}
                            onChange={e => updateSel({ locked: e.target.checked })} />
                          <label className="form-check-label small" htmlFor="lock">
                            <i className="ti ti-lock me-1" />Lock position (no drag)
                          </label>
                        </div>
                      </div>
                      {/* Pivot point — 3×3 grid for rotation origin */}
                      <div className="col-12 mt-2">
                        <label className="form-label small mb-1">Pivot point</label>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 24px)", gap: 2 }}>
                          {[
                            ["top left","TL"],["top center","TC"],["top right","TR"],
                            ["center left","ML"],["center center","C"],["center right","MR"],
                            ["bottom left","BL"],["bottom center","BC"],["bottom right","BR"],
                          ].map(([val, lbl]) => (
                            <button
                              key={val}
                              type="button"
                              title={val}
                              className={`btn btn-sm p-0 ${(sel.pivotOrigin || "center center") === val ? "btn-primary" : "btn-outline-secondary"}`}
                              style={{ width: 24, height: 24, fontSize: 9 }}
                              onClick={() => updateSel({ pivotOrigin: val })}
                            >{lbl}</button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {sel.type === "text" && (
                      <>
                        <label className="form-label small mb-0">Content</label>
                        <textarea className="form-control form-control-sm mb-2" rows={3} value={sel.content || ""}
                          onChange={e => updateSel({ content: e.target.value })} />
                        <label className="form-label small mb-0">Insert Field</label>
                        <select className="form-select form-select-sm mb-2" value="" onChange={e => e.target.value && insertField(e.target.value)}>
                          <option value="">+ dynamic field…</option>
                          {fieldsForType(certType).map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
                        </select>
                      </>
                    )}

                    {sel.type === "dynamic-text" && (
                      <>
                        <label className="form-label small mb-0">Field</label>
                        <select className="form-select form-select-sm mb-2" value={sel.field} onChange={e => updateSel({ field: e.target.value })}>
                          {fieldsForType(certType).map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
                        </select>
                        <div className="row g-2 mb-2">
                          <div className="col-6">
                            <label className="form-label small mb-0">Prefix</label>
                            <input className="form-control form-control-sm" value={sel.prefix || ""} onChange={e => updateSel({ prefix: e.target.value })} />
                          </div>
                          <div className="col-6">
                            <label className="form-label small mb-0">Suffix</label>
                            <input className="form-control form-control-sm" value={sel.suffix || ""} onChange={e => updateSel({ suffix: e.target.value })} />
                          </div>
                        </div>
                      </>
                    )}

                    {isText && (
                      <>
                        <div className="row g-2">
                          <div className="col-8">
                            <label className="form-label small mb-0">Font</label>
                            <select className="form-select form-select-sm" value={sel.fontFamily} onChange={e => updateSel({ fontFamily: e.target.value })}>
                              {BUILTIN_FONTS.map(f => <option key={f} value={f}>{f.split(",")[0]}</option>)}
                              {fonts.map(f => <option key={f._id} value={f.family}>{f.name}</option>)}
                            </select>
                          </div>
                          <div className="col-4">
                            <label className="form-label small mb-0">Size</label>
                            <input type="number" className="form-control form-control-sm" value={sel.fontSize}
                              onChange={e => updateSel({ fontSize: Number(e.target.value) })} />
                          </div>
                        </div>
                        <div className="d-flex align-items-center gap-2 mt-2">
                          <input type="color" className="form-control form-control-sm form-control-color" value={sel.color}
                            onChange={e => updateSel({ color: e.target.value })} title="Color" />
                          <button className={`btn btn-sm ${sel.fontWeight === "bold" ? "btn-primary" : "btn-outline-secondary"}`}
                            onClick={() => updateSel({ fontWeight: sel.fontWeight === "bold" ? "normal" : "bold" })}><b>B</b></button>
                          <button className={`btn btn-sm ${sel.fontStyle === "italic" ? "btn-primary" : "btn-outline-secondary"}`}
                            onClick={() => updateSel({ fontStyle: sel.fontStyle === "italic" ? "normal" : "italic" })}><i>I</i></button>
                          <select className="form-select form-select-sm" value={sel.textAlign} onChange={e => updateSel({ textAlign: e.target.value })} style={{ width: 90 }}>
                            <option value="left">Left</option>
                            <option value="center">Center</option>
                            <option value="right">Right</option>
                          </select>
                        </div>
                        <div className="form-check mt-2">
                          <input className="form-check-input" type="checkbox" checked={!!sel.autoFit} id="autofit"
                            onChange={e => updateSel({ autoFit: e.target.checked })} />
                          <label className="form-check-label small" htmlFor="autofit">Auto-fit text to box</label>
                        </div>

                        <div className="row g-2 mt-1">
                          <div className="col-6">
                            <label className="form-label small mb-0">Line height</label>
                            <input type="number" step={0.05} className="form-control form-control-sm" value={sel.lineHeight ?? 1.15}
                              onChange={e => updateSel({ lineHeight: Number(e.target.value) })} />
                          </div>
                          <div className="col-6">
                            <label className="form-label small mb-0">Letter spacing</label>
                            <input type="number" step={0.5} className="form-control form-control-sm" value={sel.letterSpacing ?? 0}
                              onChange={e => updateSel({ letterSpacing: Number(e.target.value) })} />
                          </div>
                        </div>

                        {/* Text shadow */}
                        <div className="form-check mt-2">
                          <input className="form-check-input" type="checkbox" id="txtshadow" checked={!!sel.shadow?.enabled}
                            onChange={e => updateSel({ shadow: { x: 2, y: 2, blur: 3, color: "#333333", ...(sel.shadow || {}), enabled: e.target.checked } })} />
                          <label className="form-check-label small" htmlFor="txtshadow">Text shadow</label>
                        </div>
                        {sel.shadow?.enabled && (
                          <div className="row g-1 mt-1">
                            <div className="col-3"><label className="form-label small mb-0">X</label><input type="number" className="form-control form-control-sm" value={sel.shadow.x ?? 0} onChange={e => updateSel({ shadow: { ...sel.shadow, x: Number(e.target.value) } })} /></div>
                            <div className="col-3"><label className="form-label small mb-0">Y</label><input type="number" className="form-control form-control-sm" value={sel.shadow.y ?? 0} onChange={e => updateSel({ shadow: { ...sel.shadow, y: Number(e.target.value) } })} /></div>
                            <div className="col-3"><label className="form-label small mb-0">Blur</label><input type="number" className="form-control form-control-sm" value={sel.shadow.blur ?? 0} onChange={e => updateSel({ shadow: { ...sel.shadow, blur: Number(e.target.value) } })} /></div>
                            <div className="col-3"><label className="form-label small mb-0">Color</label><input type="color" className="form-control form-control-sm form-control-color" value={asHex(sel.shadow.color)} onChange={e => updateSel({ shadow: { ...sel.shadow, color: e.target.value } })} /></div>
                          </div>
                        )}

                        {/* Text outline (stroke) */}
                        <div className="form-check mt-2">
                          <input className="form-check-input" type="checkbox" id="txtstroke" checked={!!sel.stroke?.enabled}
                            onChange={e => updateSel({ stroke: { width: 1, color: "#000000", ...(sel.stroke || {}), enabled: e.target.checked } })} />
                          <label className="form-check-label small" htmlFor="txtstroke">Text outline</label>
                        </div>
                        {sel.stroke?.enabled && (
                          <div className="d-flex gap-2 mt-1">
                            <div><label className="form-label small mb-0">Width</label><input type="number" className="form-control form-control-sm" style={{ width: 70 }} value={sel.stroke.width ?? 1} onChange={e => updateSel({ stroke: { ...sel.stroke, width: Number(e.target.value) } })} /></div>
                            <div><label className="form-label small mb-0">Color</label><input type="color" className="form-control form-control-sm form-control-color" value={asHex(sel.stroke.color)} onChange={e => updateSel({ stroke: { ...sel.stroke, color: e.target.value } })} /></div>
                          </div>
                        )}

                        {/* Gradient fill — replaces flat text color when enabled.
                            Stored as gradient.{enabled,type,from,to,angle}. The render
                            engine picks this up via CSS background-clip:text. */}
                        <div className="form-check mt-2">
                          <input className="form-check-input" type="checkbox" id="txtgrad" checked={!!sel.gradient?.enabled}
                            onChange={e => updateSel({ gradient: { type: "linear", from: "#111827", to: "#2563eb", angle: 90, ...(sel.gradient || {}), enabled: e.target.checked } })} />
                          <label className="form-check-label small" htmlFor="txtgrad">Text gradient</label>
                        </div>
                        {sel.gradient?.enabled && (
                          <div className="d-flex flex-wrap gap-2 mt-1 align-items-end">
                            <div><label className="form-label small mb-0">From</label><input type="color" className="form-control form-control-sm form-control-color" value={asHex(sel.gradient.from)} onChange={e => updateSel({ gradient: { ...sel.gradient, from: e.target.value } })} /></div>
                            <div><label className="form-label small mb-0">To</label><input type="color" className="form-control form-control-sm form-control-color" value={asHex(sel.gradient.to)} onChange={e => updateSel({ gradient: { ...sel.gradient, to: e.target.value } })} /></div>
                            <div><label className="form-label small mb-0">Angle°</label><input type="number" className="form-control form-control-sm" style={{ width: 72 }} value={sel.gradient.angle ?? 90} onChange={e => updateSel({ gradient: { ...sel.gradient, angle: Number(e.target.value) } })} /></div>
                          </div>
                        )}
                      </>
                    )}

                    {sel.type === "image" && (
                      <>
                        <label className="form-label small mb-0">Image</label>
                        <input type="file" accept="image/*" className="form-control form-control-sm mb-2"
                          onChange={e => e.target.files?.[0] && onUploadImage(e.target.files[0])} />
                        <label className="form-label small mb-0">Fit</label>
                        <select className="form-select form-select-sm" value={sel.fit} onChange={e => updateSel({ fit: e.target.value })}>
                          <option value="contain">Contain</option>
                          <option value="cover">Cover</option>
                          <option value="fill">Fill</option>
                        </select>
                      </>
                    )}

                    {sel.type === "signature" && (
                      <>
                        <label className="form-label small mb-0">Signature (or leave as slot)</label>
                        <select className="form-select form-select-sm mb-2" value={sel.signatureId || ""} onChange={e => updateSel({ signatureId: e.target.value })}>
                          <option value="">(pick at generate time)</option>
                          {signatures.map(s => <option key={s._id} value={s._id}>{s.signerName}</option>)}
                        </select>
                        <div className="form-check">
                          <input className="form-check-input" type="checkbox" checked={!!sel.showName} onChange={e => updateSel({ showName: e.target.checked })} id="sn" />
                          <label className="form-check-label small" htmlFor="sn">Show name</label>
                        </div>
                        <div className="form-check">
                          <input className="form-check-input" type="checkbox" checked={!!sel.showTitle} onChange={e => updateSel({ showTitle: e.target.checked })} id="st" />
                          <label className="form-check-label small" htmlFor="st">Show title</label>
                        </div>
                      </>
                    )}

                    {sel.type === "shape" && (
                      <>
                        <label className="form-label small mb-0">Shape</label>
                        <select className="form-select form-select-sm mb-2" value={sel.shape} onChange={e => updateSel({ shape: e.target.value })}>
                          <option value="rectangle">Rectangle</option>
                          <option value="ellipse">Ellipse</option>
                        </select>
                        <div className="d-flex gap-2 align-items-center">
                          <div><label className="form-label small mb-0">Fill</label><input type="color" className="form-control form-control-sm form-control-color" value={sel.fill === "transparent" ? "#ffffff" : (sel.fill || "#ffffff")} onChange={e => updateSel({ fill: e.target.value })} /></div>
                          <div><label className="form-label small mb-0">Stroke</label><input type="color" className="form-control form-control-sm form-control-color" value={asHex(sel.stroke)} onChange={e => updateSel({ stroke: e.target.value })} /></div>
                          <div><label className="form-label small mb-0">Width</label><input type="number" className="form-control form-control-sm" style={{ width: 70 }} value={sel.strokeWidth ?? 0} onChange={e => updateSel({ strokeWidth: Number(e.target.value) })} /></div>
                        </div>
                        {/* Shape gradient fill */}
                        <div className="form-check mt-2">
                          <input className="form-check-input" type="checkbox" id="shpgrad" checked={!!sel.gradient?.enabled}
                            onChange={e => updateSel({ gradient: { type: "linear", from: "#2563eb", to: "#7c3aed", angle: 135, ...(sel.gradient || {}), enabled: e.target.checked } })} />
                          <label className="form-check-label small" htmlFor="shpgrad">Gradient fill</label>
                        </div>
                        {sel.gradient?.enabled && (
                          <div className="d-flex flex-wrap gap-2 mt-1 align-items-end">
                            <div><label className="form-label small mb-0">From</label><input type="color" className="form-control form-control-sm form-control-color" value={asHex(sel.gradient.from)} onChange={e => updateSel({ gradient: { ...sel.gradient, from: e.target.value } })} /></div>
                            <div><label className="form-label small mb-0">To</label><input type="color" className="form-control form-control-sm form-control-color" value={asHex(sel.gradient.to)} onChange={e => updateSel({ gradient: { ...sel.gradient, to: e.target.value } })} /></div>
                            <div><label className="form-label small mb-0">Angle°</label><input type="number" className="form-control form-control-sm" style={{ width: 72 }} value={sel.gradient.angle ?? 135} onChange={e => updateSel({ gradient: { ...sel.gradient, angle: Number(e.target.value) } })} /></div>
                          </div>
                        )}
                      </>
                    )}

                    {sel.type === "line" && (
                      <div className="d-flex gap-2 align-items-center">
                        <div><label className="form-label small mb-0">Color</label><input type="color" className="form-control form-control-sm form-control-color" value={sel.color} onChange={e => updateSel({ color: e.target.value })} /></div>
                        <div><label className="form-label small mb-0">Thickness</label><input type="number" className="form-control form-control-sm" style={{ width: 80 }} value={sel.thickness} onChange={e => updateSel({ thickness: Number(e.target.value) })} /></div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CertDesigner
